import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { Layout } from '@/components/layout';
import { Card, Button, Spinner, ErrorState, Textarea, Badge } from '@/components/atoms';
import { useBackButton } from '@/hooks';
import { reverse } from '@/api';
import type { ReverseRequest } from '@/types/domain';
import { toast } from '@/components/atoms/Toast';
import { logger } from '@/utils/logger';
import styles from './pages.module.css';

export function ReverseRequestDetailPage(): JSX.Element {
  useBackButton();
  const { id = '' } = useParams<{ id: string }>();
  const reqId = Number(id);
  const [item, setItem] = useState<ReverseRequest | null>(null);
  const [errored, setErrored] = useState(false);
  const [response, setResponse] = useState('');

  useEffect(() => {
    if (!Number.isFinite(reqId)) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await reverse.fetchReverseRequest(reqId);
        if (!cancelled) setItem(data);
      } catch (err) {
        logger.warn('Reverse detail failed', err);
        if (!cancelled) setErrored(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reqId]);

  if (errored) return <ErrorState onRetry={() => setErrored(false)} />;
  if (!item)
    return (
      <Layout title="Запит">
        <Spinner center />
      </Layout>
    );

  const send = async (): Promise<void> => {
    if (!response.trim()) return;
    try {
      await reverse.respondReverseRequest(item.id, response);
      toast.success('Відповідь надіслано');
      setResponse('');
    } catch (err) {
      logger.error('Respond failed', err);
      toast.error('Не вдалось');
    }
  };

  return (
    <Layout title="Запит">
      <Card>
        <strong>
          {item.category.icon} {item.category.name_ua} · {item.city.name_ua}
        </strong>
        <p style={{ marginTop: 8, marginBottom: 0 }}>
          {item.is_anonymous ? (
            <Badge icon="user-x" variant="muted">
              Анонімно
            </Badge>
          ) : (
            item.client_first_name
          )}
        </p>
        <p style={{ marginTop: 8 }}>{item.description}</p>
        <div className={styles.kvList} style={{ marginTop: 12 }}>
          {item.budget_eur_max ? (
            <>
              <span className={styles.kvKey}>Бюджет</span>
              <span>{item.budget_eur_max} €</span>
            </>
          ) : null}
          {item.desired_date ? (
            <>
              <span className={styles.kvKey}>Бажана дата</span>
              <span>{dayjs(item.desired_date).format('D MMM YYYY')}</span>
            </>
          ) : null}
          <span className={styles.kvKey}>Відповідей</span>
          <span>{item.response_count}</span>
        </div>
      </Card>

      <Card>
        <Textarea
          label="Твоя відповідь"
          value={response}
          onChange={(e) => setResponse(e.target.value)}
          rows={3}
          maxLength={1000}
        />
        <Button onClick={send} disabled={!response.trim()} style={{ marginTop: 8 }}>
          Відгукнутися
        </Button>
      </Card>
    </Layout>
  );
}
